import {
  action,
  computed,
  IReactionDisposer,
  makeObservable,
  observable,
  reaction,
  toJS,
} from 'mobx';

import { FieldDataEntity, FieldDataEntityApi } from 'entities/FieldData';
import { FieldDefaultValueEntity } from 'entities/FieldDefaultValue';
import {
  FillingMethod,
  FormFieldEntity,
  FormFieldEntityApi,
  normalizeFormFieldEntity,
} from 'entities/FormField';
import { FieldType } from 'entities/common';
import { FormFieldEntityApiParams } from 'entities/endpoints/form.update_form';
import { ToggleStateStore } from 'stores/common';
import { ILocalStore } from 'stores/interfaces';
import { EntrySelect } from 'stores/models/EntrySelect';
import { EntryText } from 'stores/models/EntryText';
import { API_FORMULA_START } from 'stores/models/NewFormulaModel/normalize';
import { Optional } from 'utils/types';
import { validateNotEmpty } from 'utils/validators';

import IdGenerator from './IdGenerator';
import {
  FORM_FIELD_DEFAULT_VALUES,
  isDataTypeWithoutFillingMethod,
  PIVOT_SCREEN_TOGGLE_AVAILABLE_DATA_TYPES,
} from './config';
import { IFormFieldModel } from './interface';
import { FormFieldSource } from './types';

type PrivateFields =
  | '_fillingMethod'
  | '_defaultValue'
  | '_fillingMethodToServerFormat';

type FormFieldEntityParams = FormFieldEntity & {
  formFieldCreationSource: FormFieldSource;
};

/** Стор модели */
export class FormFieldModel
  implements FormFieldEntity, IFormFieldModel, ILocalStore
{
  constructor(params: FormFieldEntityParams) {
    this.id = params.id;
    this._fillingMethod = params.fillingMethod;
    this._defaultValue = params.defaultValue;
    this._formFieldCreationSource = params.formFieldCreationSource;

    this.fieldData = params.fieldData;
    this.defaultVisibilityToggleModel = new ToggleStateStore({
      defaultValue: params.defaultVisibility,
    });
    this.supervisorVisibilityToggleModel = new ToggleStateStore({
      defaultValue: params.supervisorVisibility,
    });
    this.pivotScreenVisibilityToggleModel = new ToggleStateStore({
      defaultValue: params.pivotScreenVisibility,
    });
    this.importEditableToggleModel = new ToggleStateStore({
      defaultValue: params.importEditable,
    });
    this.formId = params.formId;

    this.nameModel = new EntryText({
      required: true,
      /** Валидируем только поля, создаваемые на фронте ( кастомные )*/
      validator:
        params.formFieldCreationSource ===
        FormFieldSource.CUSTOM_FIELD_NOT_SAVED
          ? validateNotEmpty()
          : undefined,
    });

    this._reactions.push(
      reaction(
        () => this.supervisorVisibilityToggleModel.opened,
        (state) => {
          if (!state) {
            this.defaultVisibilityToggleModel.close();
            this.defaultVisibilityToggleModel.disable();

            return;
          }

          this.defaultVisibilityToggleModel.enable();
        },
        {
          fireImmediately: true,
        }
      )
    );
    this._handlePivotScreenVisibilityReaction =
      this._handlePivotScreenVisibilityReaction.bind(this);

    this._reactions.push(
      reaction(
        () => this.selectModel.value,
        this._handlePivotScreenVisibilityReaction
      )
    );
    this._reactions.push(
      reaction(
        () => this.fieldData.dataType,
        this._handlePivotScreenVisibilityReaction,
        {
          fireImmediately: true,
        }
      )
    );

    makeObservable<FormFieldModel, PrivateFields>(this, {
      _fillingMethod: observable,
      _defaultValue: observable.ref,

      fillingMethod: computed,
      setFillingMethod: action.bound,
      _fillingMethodToServerFormat: computed,

      defaultVisibility: computed,
      supervisorVisibility: computed,
      pivotScreenVisibility: computed,
      importEditable: computed,

      relatedCatalogFieldId: computed,

      fieldDataToServerFormat: computed,
      toServerFormat: computed,

      defaultValue: computed,

      custom: computed,
      fromCatalog: computed,
      saved: computed,
      hasUnfilledFormula: computed,
    });
  }

  private readonly _reactions: IReactionDisposer[] = [];

  /**
   * id поля в форме (связь поле-форма )
   */
  readonly id: string;
  /**
   * Инфа о поле из каталога
   * (ее не хаватает чтобы отобразить строчку, поэтому необходимо по id сделать join с каталогом
   *)
   */
  readonly fieldData: FieldDataEntity;

  readonly nameModel: EntryText;

  readonly selectModel = new EntrySelect<FieldType>({
    options: Object.values(FieldType),
    defaultValue: FieldType.str,
  });

  private _fillingMethod: FillingMethod;

  private _defaultValue: FieldDefaultValueEntity | null = null;

  private readonly _formFieldCreationSource: FormFieldSource;

  readonly defaultVisibilityToggleModel: ToggleStateStore;

  readonly supervisorVisibilityToggleModel: ToggleStateStore;

  readonly pivotScreenVisibilityToggleModel: ToggleStateStore;

  readonly importEditableToggleModel: ToggleStateStore;

  readonly formId: number;

  get defaultValue(): FieldDefaultValueEntity | null {
    return this._defaultValue;
  }

  get hasUnfilledFormula(): boolean {
    if (this.fieldData.dataType !== FieldType.formula) {
      return false;
    }

    /** Считаем, что формула незаполнена, если:
     * 1. Нет значения вообще
     * 2. Набирали значение, но стёрли его до пустой строки ( из-за нормализации всегда первым символом будет API_FORMULA_START)
     * */
    return (
      !this.defaultValue ||
      this.defaultValue?.value.length === 0 ||
      this.defaultValue.value === API_FORMULA_START
    );
  }

  get custom() {
    return (
      this._formFieldCreationSource === FormFieldSource.CUSTOM_FIELD_NOT_SAVED
    );
  }

  get fromCatalog(): boolean {
    return (
      this._formFieldCreationSource === FormFieldSource.FROM_CATALOG_NOT_SAVED
    );
  }

  get saved(): boolean {
    return !(this.custom || this.fromCatalog);
  }

  get fillingMethod(): FillingMethod {
    return this._fillingMethod;
  }

  get defaultVisibility(): boolean {
    return this.defaultVisibilityToggleModel.opened;
  }

  get supervisorVisibility(): boolean {
    return this.supervisorVisibilityToggleModel.opened;
  }

  get pivotScreenVisibility(): boolean {
    return this.pivotScreenVisibilityToggleModel.opened;
  }

  get importEditable(): boolean {
    return this.importEditableToggleModel.opened;
  }

  /**
   * id поля из каталога
   */
  get relatedCatalogFieldId(): number {
    return this.fieldData.id;
  }

  get fieldDataToServerFormat(): Optional<FieldDataEntityApi, 'id'> {
    if (this.custom) {
      return {
        name: this.nameModel.toServerFormat as string,
        data_type: this.selectModel.toServerFormat as FieldType,
      };
    }

    return {
      name: this.fieldData.name,
      id: this.fieldData.id,
      data_type: this.fieldData.dataType,
    };
  }

  /** todo: вычистить костыль после фикса на бэке
   * Сейчас хардкодом мапим нужный тип заполнения
   * */
  private get _fillingMethodToServerFormat(): FillingMethod {
    if (isDataTypeWithoutFillingMethod(this)) {
      return FillingMethod.form;
    }

    return this._fillingMethod;
  }

  get toServerFormat(): FormFieldEntityApiParams {
    return toJS({
      pivot_screen_visibility: this.pivotScreenVisibility,
      field_data: this.fieldDataToServerFormat,
      id: IdGenerator.isGeneratedId(this.id) ? undefined : this.id,
      form_id: this.formId,
      supervisor_visibility: this.supervisorVisibility,
      filling_method: this._fillingMethodToServerFormat,
      default_visibility: this.defaultVisibility,
      import_editable: this.importEditable,
      default_value: this.defaultValue,
    });
  }

  private _handlePivotScreenVisibilityReaction(value: FieldType) {
    if (!PIVOT_SCREEN_TOGGLE_AVAILABLE_DATA_TYPES.includes(value)) {
      this.pivotScreenVisibilityToggleModel.close();
      this.pivotScreenVisibilityToggleModel.disable();

      return;
    }

    this.pivotScreenVisibilityToggleModel.enable();
  }

  setFillingMethod(newVal: FillingMethod): void {
    this._fillingMethod = newVal;
  }

  setDefaultValue(newVal: FieldDefaultValueEntity): void {
    this._defaultValue = newVal;
  }

  /** Конструктор для модели поля, который используется при добавлении поля из каталога */
  static createFromCatalog(
    formId: number,
    catalogFieldData: FieldDataEntity
  ): FormFieldModel {
    return new FormFieldModel({
      supervisorVisibility: FORM_FIELD_DEFAULT_VALUES.SUPERVISOR_VISIBILITY,
      pivotScreenVisibility: FORM_FIELD_DEFAULT_VALUES.PIVOT_SCREEN_VISIBILITY,
      defaultVisibility: FORM_FIELD_DEFAULT_VALUES.DEFAULT_VISIBILITY,
      importEditable: FORM_FIELD_DEFAULT_VALUES.IMPORT_EDITABLE,
      fillingMethod: FORM_FIELD_DEFAULT_VALUES.FILLING_METHOD,
      defaultValue: FORM_FIELD_DEFAULT_VALUES.DEFAULT_VALUE,
      fieldData: catalogFieldData,
      id: IdGenerator.generateId(),
      formFieldCreationSource: FormFieldSource.FROM_CATALOG_NOT_SAVED,
      formId,
    });
  }

  /** Конструктор для модели поля, который используется при создании поля пользователем на клиенте */
  static createCustomField(formId: number): FormFieldModel {
    return new FormFieldModel({
      id: IdGenerator.generateId(),
      supervisorVisibility: FORM_FIELD_DEFAULT_VALUES.SUPERVISOR_VISIBILITY,
      pivotScreenVisibility: FORM_FIELD_DEFAULT_VALUES.PIVOT_SCREEN_VISIBILITY,
      defaultVisibility: FORM_FIELD_DEFAULT_VALUES.DEFAULT_VISIBILITY,
      importEditable: FORM_FIELD_DEFAULT_VALUES.IMPORT_EDITABLE,
      fillingMethod: FORM_FIELD_DEFAULT_VALUES.FILLING_METHOD,
      defaultValue: FORM_FIELD_DEFAULT_VALUES.DEFAULT_VALUE,
      formId,
      formFieldCreationSource: FormFieldSource.CUSTOM_FIELD_NOT_SAVED,
      fieldData: {
        id: IdGenerator.generateCatalogFieldId(),
        dataType: FORM_FIELD_DEFAULT_VALUES.DATA_TYPE,
        name: FORM_FIELD_DEFAULT_VALUES.NAME,
      },
    });
  }

  static createFromJSON(raw: FormFieldEntityApi): FormFieldModel {
    return new FormFieldModel({
      ...normalizeFormFieldEntity(raw),
      formFieldCreationSource: FormFieldSource.FIELD_SAVED,
    });
  }

  destroy() {
    this.supervisorVisibilityToggleModel.destroy();
    this.defaultVisibilityToggleModel.destroy();
    this.pivotScreenVisibilityToggleModel.destroy();
    this.importEditableToggleModel.destroy();
    this._reactions.forEach((reaction) => reaction());
  }
}
